<template>
  <div class="step-editor mb-1">
    <div class="step-row p-1 border-bottom">
      <span class="mr-2 intensity-icon">
        <font-awesome-icon
            v-if="!isRepeat"
            :icon="intensityIcon"
            :class="{clickable: !readOnly}"
            :title="step.intensity"
            @click="!readOnly && cycleIntensity()"
        />
        <font-awesome-icon v-else icon="redo" title="Repeat"/>
      </span>

      <span v-if="readOnly" class="mr-3 value-control">
        {{ isRepeat ? `× ${step.repeatValue}` : `${step.durationValue} ${step.durationType === 'TIME' ? 'sec' : 'm'}` }}
      </span>
      <span v-else class="mr-3 value-control">
        <b-input-group v-if="!isRepeat" size="sm">
          <b-form-input
              type="number"
              min="0"
              :value="step.durationValue"
              @input="onDurationValueChange"
          />
          <b-input-group-append>
            <b-input-group-text class="unit-label">{{ step.durationType === 'TIME' ? 'sec' : 'm' }}</b-input-group-text>
            <b-button size="sm" variant="outline-secondary" title="Toggle time/distance" @click="toggleDurationType">
              <font-awesome-icon icon="exchange-alt"/>
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <b-input-group v-else size="sm" prepend="×">
          <b-form-input
              type="number"
              min="1"
              :value="step.repeatValue"
              @input="onRepeatValueChange"
          />
        </b-input-group>
      </span>

      <span v-if="!isRepeat" class="mr-3 zones">
        <span
            v-for="zone in 5"
            :key="zone"
            class="zone-toggle"
            :class="[step.zones.includes(zone) ? `zone${zone}` : 'inactive', {'read-only': readOnly}]"
            @click="!readOnly && toggleZone(zone)"
        >{{ zone }}</span>
      </span>

      <span v-if="!readOnly" class="ml-auto actions">
        <font-awesome-icon
            icon="arrow-up"
            class="mr-2"
            :class="isFirst ? 'text-muted' : 'clickable'"
            @click="!isFirst && $emit('move-up')"
        />
        <font-awesome-icon
            icon="arrow-down"
            class="mr-2"
            :class="isLast ? 'text-muted' : 'clickable'"
            @click="!isLast && $emit('move-down')"
        />
        <font-awesome-icon icon="trash" class="clickable text-danger" @click="$emit('remove')"/>
      </span>
    </div>

    <div v-if="isRepeat" class="pl-2 pt-1 repeat-children">
      <WorkoutStepEditor
          v-for="(child, i) in step.steps"
          :key="i"
          :step="child"
          :read-only="readOnly"
          :is-first="i === 0"
          :is-last="i === step.steps.length - 1"
          @update="onChildUpdate(i, $event)"
          @remove="onChildRemove(i)"
          @move-up="onChildMove(i, -1)"
          @move-down="onChildMove(i, 1)"
      />
      <b-button v-if="!readOnly" size="sm" variant="outline-primary" class="mt-1 mb-2" @click="addChildStep">+ Add Step</b-button>
    </div>
  </div>
</template>

<script>
const INTENSITIES = ['WARMUP', 'ACTIVE', 'COOLDOWN'];
const INTENSITY_ICONS = {
  WARMUP: 'chevron-up',
  ACTIVE: 'running',
  COOLDOWN: 'chevron-down',
};

export default {
  name: 'WorkoutStepEditor',
  props: {
    step: Object,
    isFirst: Boolean,
    isLast: Boolean,
    // Shows the step without any editing controls
    readOnly: Boolean,
  },
  computed: {
    isRepeat() {
      return this.step.type === 'WorkoutRepeatStep';
    },
    intensityIcon() {
      return INTENSITY_ICONS[this.step.intensity] || 'running';
    },
  },
  methods: {
    emitUpdate(changes) {
      this.$emit('update', {...this.step, ...changes});
    },
    cycleIntensity() {
      const idx = INTENSITIES.indexOf(this.step.intensity);
      const next = INTENSITIES[(idx + 1) % INTENSITIES.length];
      this.emitUpdate({intensity: next});
    },
    toggleDurationType() {
      const next = this.step.durationType === 'TIME' ? 'DISTANCE' : 'TIME';
      this.emitUpdate({durationType: next});
    },
    onDurationValueChange(value) {
      this.emitUpdate({durationValue: Number(value) || 0});
    },
    onRepeatValueChange(value) {
      this.emitUpdate({repeatValue: Math.max(1, Number(value) || 1)});
    },
    toggleZone(zone) {
      const zones = this.step.zones.slice();
      const pos = zones.indexOf(zone);
      if (pos >= 0) {
        if (zones.length === 1) {
          // Keep at least one zone selected
          return;
        }
        zones.splice(pos, 1);
      } else {
        zones.push(zone);
        zones.sort((a, b) => a - b);
      }
      this.emitUpdate({zones});
    },
    addChildStep() {
      const steps = this.step.steps.concat([{
        type: 'WorkoutStep',
        intensity: 'ACTIVE',
        durationType: 'TIME',
        durationValue: 60,
        zones: [2],
      }]);
      this.emitUpdate({steps});
    },
    onChildUpdate(i, newChild) {
      const steps = this.step.steps.slice();
      steps.splice(i, 1, newChild);
      this.emitUpdate({steps});
    },
    onChildRemove(i) {
      const steps = this.step.steps.slice();
      steps.splice(i, 1);
      this.emitUpdate({steps});
    },
    onChildMove(i, direction) {
      const target = i + direction;
      if (target < 0 || target >= this.step.steps.length) return;
      const steps = this.step.steps.slice();
      [steps[i], steps[target]] = [steps[target], steps[i]];
      this.emitUpdate({steps});
    },
  },
}
</script>

<style scoped>
.clickable {
  cursor: pointer;
}

.step-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  overflow-x: auto;
}

.intensity-icon,
.value-control,
.zones,
.actions {
  flex-shrink: 0;
}

.zones {
  white-space: nowrap;
}

.actions {
  display: flex;
  align-items: center;
}

.unit-label {
  width: 32px;
  text-align: center;
  justify-content: center;
}

.zone-toggle {
  display: inline-block;
  width: 20px;
  text-align: center;
  font-weight: bold;
  cursor: pointer;
}

.zone-toggle.read-only {
  cursor: default;
}

.zone-toggle.zone1 {
  color: blue;
}

.zone-toggle.zone2 {
  color: darkblue;
}

.zone-toggle.zone3 {
  color: darkgreen;
}

.zone-toggle.zone4 {
  color: orangered;
}

.zone-toggle.zone5 {
  color: darkred;
}

.zone-toggle.inactive {
  color: #bbb;
}

.repeat-children {
  border-left: 3px solid #e4f8f8;
}

.value-control {
  width: 120px;
}

.value-control >>> input[type="number"] {
  width: 50px;
}
</style>
